import { queryRegExp } from 'common/utilities/regexp/regexp';
import type * as I from 'core/api/coreApiClient';

import { metaInfoInfos } from './table';

/**
 * Multiple filtering options logic:
 * - if no filter query set
 *   - toggled properties are always visible (to stay on screen maxShownColumns or not)
 *   - visible properties are priority
 *   - then only return all hidden properties to reach maxShownColumns
 * - if filter query set
 *   - toggled properties are always visible (to stay on screen maxShownColumns or not)
 *   - return matching infos (visible or not) up to maxShownColumns
 */

function filterGroupInfosWithQuery(
  groups: I.Group[],
  filterGroup: I.Group | undefined,
  filterQuery: string,
  /** Maximum amount of columns that should be shown to the user */
  maxShownColumns: number,
) {
  let filteredInfosCount = 0;
  let propertiesCount = 0;

  const filteredGroups = groups
    .filter((group) => (filterGroup ? filterGroup.id === group.id : true))
    .reduce<I.Group[]>((acc, group) => {
      let newGroup: I.Group | undefined;

      const filteredInfos = group.infos.filter((info) => {
        propertiesCount += 1;

        // A user should not see over maxShownColumns columns while filtering or not, unless it's an "always shown" column
        // Note: "always shown" columns are reset when a user toggles between filtering and not filtering (new context)
        if (filteredInfosCount >= maxShownColumns) {
          return false;
        }

        // A user filtering should see matched columns and groups
        if (info.label.match(queryRegExp(filterQuery))) {
          filteredInfosCount += 1;
          return true;
        }

        return false;
      });

      if (filteredInfos.length > 0) {
        newGroup = { ...group, infos: filteredInfos };
        return [...acc, newGroup];
      }

      // Check if the group label match the filter query
      // The group label also counts as a property, otherwise thousands of groups could potentially be added to the list
      if (filteredInfosCount < maxShownColumns && group.label.match(queryRegExp(filterQuery))) {
        filteredInfosCount += 1;
        newGroup = { ...group, infos: [] };
        return [...acc, newGroup];
      }

      return acc;
    }, []);

  return {
    groups: filteredGroups,
    propertiesCount,
    unshownPropertyIdsCount: propertiesCount - filteredInfosCount,
    unshownVisiblePropertiesCount: undefined,
    // Shown columns (not related to column visibility)
    maxShownColumnsReached: filteredInfosCount >= maxShownColumns,
  };
}

export function filterGroupInfos(
  groups: I.Group[],
  visiblePropertyIds: string[],
  filterGroup: I.Group | undefined,
  filterQuery: string,
  // Maximum amount of columns that should be shown to the user
  maxShownColumns: number,
) {
  if (filterGroup || filterQuery.length > 0) {
    return filterGroupInfosWithQuery(groups, filterGroup, filterQuery, maxShownColumns);
  }

  /** Options that will be shown to the user */
  let selectedPropertiesCount = 0;
  /** Visible options that will be shown to the user */
  let shownVisiblePropertiesCount = 0;
  /** Total count of properties */
  let propertiesCount = 0;

  // 1. Add all groups having at least one property visible up to maxShownColumns.
  //    Include all properties from those group (the total will probably go over maxShownColumns).
  let filteredGroups = groups.reduce<I.Group[]>((acc, group) => {
    propertiesCount += group.infos.length;

    // Enough properties
    if (selectedPropertiesCount >= maxShownColumns) return acc;

    const groupHasVisibleProperty = group.infos.find((info) => (
      visiblePropertyIds.includes(info.id)
    ));

    if (groupHasVisibleProperty) {
      selectedPropertiesCount += group.infos.length;
      shownVisiblePropertiesCount += group.infos.filter((i) => visiblePropertyIds.includes(i.id)).length;
      return [...acc, group];
    }

    return acc;
  }, []);

  // 2. If not enough property are selected, add extra groups
  if (selectedPropertiesCount < maxShownColumns) {
    filteredGroups = groups.reduce<I.Group[]>((acc, group) => {
      // Group was already selected
      const filteredGroup = filteredGroups.find((g) => g.id === group.id);
      if (filteredGroup) return [...acc, filteredGroup];

      // Enough properties
      if (selectedPropertiesCount >= maxShownColumns) return acc;

      // Add group
      selectedPropertiesCount += group.infos.length;
      return [...acc, group];
    }, []);
  }

  return {
    groups: filteredGroups,
    propertiesCount,
    unshownPropertyIdsCount: propertiesCount - selectedPropertiesCount,
    unshownVisiblePropertiesCount: visiblePropertyIds.length - shownVisiblePropertiesCount,
    // Shown columns (not related to column visibility)
    maxShownColumnsReached: selectedPropertiesCount >= maxShownColumns,
  };
}

/**
 * Filter group infos based on the filter query and return matching ids.
 * A group name matching the filter query will not influence the selection.
 */
export function filterGroupInfosWithQueryAndReturnMatchingIds(
  groups: I.Group[],
  filterGroup: I.Group | undefined,
  filterQuery: string,
) {
  const { groups: filteredGroups } = filterGroupInfosWithQuery(groups, filterGroup, filterQuery, Infinity);
  return metaInfoInfos({ groups: filteredGroups }).map((i) => i.id);
}
